import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AuthUser, LoginRequest } from "@/contracts";

import type { Session } from "@/lib/api-client";
import { useApiClient } from "@/lib/api-context";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./auth-context";

/**
 * How early to renew the access token, in milliseconds.
 *
 * Refreshing ahead of expiry means the common case never pays for a failed
 * request and a retry. The margin also absorbs clock skew between the browser
 * and the API.
 */
const RENEW_MARGIN_MS = 60_000;
const MIN_RENEW_DELAY_MS = 5_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const queryClient = useQueryClient();
  const renewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRenewTimer = useCallback(() => {
    if (renewTimer.current !== null) {
      clearTimeout(renewTimer.current);
      renewTimer.current = null;
    }
  }, []);

  const scheduleRenew = useCallback(
    (session: Session) => {
      clearRenewTimer();

      const delay = Math.max(session.expiresIn * 1_000 - RENEW_MARGIN_MS, MIN_RENEW_DELAY_MS);

      renewTimer.current = setTimeout(() => {
        // A failure here ends the session through the same subscription as
        // any other refresh, so there is nothing to handle locally.
        void client.restore();
      }, delay);
    },
    [client, clearRenewTimer],
  );

  // One subscription owns every transition: login, silent renewal, logout and
  // a refresh the API refused. Screens never set auth state themselves.
  useEffect(() => {
    const unsubscribe = client.subscribe((session) => {
      if (session) {
        setUser(session.user);
        setStatus("authenticated");
        scheduleRenew(session);

        return;
      }

      clearRenewTimer();
      setUser(null);
      setStatus("anonymous");
      // Another user must never see the previous one's cached lists.
      queryClient.clear();
    });

    return unsubscribe;
  }, [client, clearRenewTimer, queryClient, scheduleRenew]);

  // Cold start: the access token died with the previous page, but the refresh
  // cookie may still be good.
  useEffect(() => {
    let cancelled = false;

    void client.restore().then((session) => {
      if (cancelled) {
        return;
      }

      setUser(session?.user ?? null);
      setStatus(session ? "authenticated" : "anonymous");
    });

    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => clearRenewTimer, [clearRenewTimer]);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      await client.login(credentials);
    },
    [client],
  );

  const logout = useCallback(async () => {
    await client.logout();
  }, [client]);

  const logoutAll = useCallback(async () => {
    await client.logoutAll();
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, logoutAll }),
    [status, user, login, logout, logoutAll],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
